import { Product, ProductPrice } from '~~/shared/entities/commerce/Product';
import type { ProductId } from '~~/shared/types/Products';
import type { PymentKnownFlow } from '~~/shared/types/Payments';
import type { AccumulationGroup, VolumeDiscountConfig } from '~~/shared/types/VolumeDiscount';

export class ProductManager {

  private wsscPro!: Product;
  private wsVpnGw!: Product;
  private paddleClientToken!: string;
  private env: 'sandbox' | 'production' = 'sandbox';
  private volumeDiscountConfig!: VolumeDiscountConfig;

  /**
   * Products whose seats accumulate together for the cumulative partner discount
   * (011-partner-seat-volume-discount).
   *
   * A product may appear in at most one group. A product listed in no group is
   * excluded from accumulation: its purchases are priced by order size only and
   * its seats never contribute to any accumulated total.
   *
   * Changing this configuration only affects purchases made afterwards — the
   * group is resolved when a checkout is created and never re-evaluated.
   */
  private accumulationGroups: AccumulationGroup[] = [
    {
      id: 'pro-seats',
      productIds: ['wiresock-secure-connect-pro', 'wiresock-vpn-gateway-pro'],
    },
  ];

  private constructor() {
  }

  public static async createProductManager(env: 'sandbox' | 'production') {
    const productManager = new ProductManager();
    productManager.env = env;
    productManager.volumeDiscountConfig = {
      enabled: true,
      tiers: [
        {
          minSeats: 10,
          maxSeats: 19,
          percentage: 10,
          label: '10–19 users: 10% off',
          sandboxDiscountId: 'dsc_01kdxn81bxw63t3r94s1xcwbjw',
          productionDiscountId: 'dsc_01kj34npac71a3mk61pcec64v5',
        },
        {
          minSeats: 20,
          maxSeats: 49,
          percentage: 20,
          label: '20–49 users: 20% off',
          sandboxDiscountId: 'dsc_01kdxpf2rjx82g5zha624n10k1',
          productionDiscountId: 'dsc_01kj34t47cvwta3b1mer2f2hxv',
        },
        {
          minSeats: 50,
          maxSeats: 99,
          percentage: 25,
          label: '50–99 users: 25% off',
          sandboxDiscountId: 'dsc_01kdxpfkbty5rptvjt4a2jyn38',
          productionDiscountId: 'dsc_01kj34va0d867e6ch6ke3zvy0k',
        },
        {
          minSeats: 100,
          maxSeats: 199,
          percentage: 30,
          label: '100–199 users: 30% off',
          sandboxDiscountId: 'dsc_01kdxpg5kz7485z7xsw1xg1qbc',
          productionDiscountId: 'dsc_01kj34wsh840sffb1r8ev94mqf',
        },
        {
          minSeats: 200,
          maxSeats: null,
          percentage: 40,
          label: '200+ users: 40% off',
          sandboxDiscountId: 'dsc_01kdxphbdptcze0hyn774h6fky',
          productionDiscountId: 'dsc_01kj34yat8dbf1n9e080avy9pe',
        },
      ],
    };


    if (env === 'sandbox') {
      productManager.paddleClientToken = 'test_2b8ae6c089618be2846f3812035';
      productManager.wsscPro = new Product('wiresock-secure-connect-pro', [
        new ProductPrice(
          'pri_01k90pxaxgx2rd6nzqg3t5qh1s',
          '4000',
          'USD',
          'perpetual_only', // perpetual without Support&updates. This price doesn't contain price for S&U license. We use this price when a customer buys a perpetual license and a subscription (support&updates) as two items in a single transaction.
          'one_time',
        ),
        new ProductPrice(
          'pri_01jwm2x6x8679x69fy6m9g6m73',
          '1000',
          'USD',
          'updates_1year', // maintenance overlay (subscription)
          'recurring',
        ),
        new ProductPrice(
          'pri_01jw46rgctk9z0an8k9naw2v1n',
          '500',
          'USD',
          'subscription_1month',
          'recurring',
        ),
        new ProductPrice(
          'pri_01jw46fx3cwkm5jdz0ztex3dg1',
          '200',
          'USD',
          'subscription_1day',
          'recurring',
        ),
      ], {
        paddleId: 'pro_01jvn85r12f42d2gyy1k3pfj76',
      });

      productManager.wsVpnGw = new Product(
        'wiresock-vpn-gateway-pro', [
          new ProductPrice(
            'pri_01jxgba9ak8ramgsw0erd5ppc5',
            '9950',
            'USD',
            'perpetual_only',
            'one_time'),
        ], {
          paddleId: 'pro_01jxgb5bmb068z4v094v6mrmb0',
        },
      );
    } else {
      productManager.paddleClientToken = 'live_e1a4be5edac29b17b08ceeb9300';
      productManager.wsscPro = new Product('wiresock-secure-connect-pro', [
        new ProductPrice(
          'pri_01jvfzng427da84skds0ta8v3h',
          '4000',
          'USD',
          'perpetual_only', // perpetual without Support&updates. This price doesn't contain price for S&U license. We use this price when a customer buys a perpetual license and a subscription (support&updates) as two items in a single transaction.
          'one_time',
        ),
        new ProductPrice(
          'pri_01jvfzq89zygyhh2gd0a79fmc4',
          '1000',
          'USD',
          'updates_1year', // maintenance overlay (subscription)
          'recurring',
        ),
      ], {
        paddleId: 'pro_01jvfzk31bnf5xqdfpz4pkptc0',
      });

      productManager.wsVpnGw = new Product(
        'wiresock-vpn-gateway-pro', [
          new ProductPrice(
            'pri_01jvfz9eqwjfw7qaqm7143vw71',
            '10000',
            'USD',
            'perpetual_only',
            'one_time'),
        ], {
          paddleId: 'pro_01jv9h9wzh7wd3g09zynpsgmca',
        },
      );
    }
    return productManager;
  }

  public getWsscPro() {
    return this.wsscPro;
  }

  public getWsVpnGw() {
    return this.wsVpnGw;
  }

  public getPaddleClientToken() {
    return this.paddleClientToken;
  }

  public getEnv() {
    return this.env;
  }

  getProductByPaddleId(paddleProductId: string) {
    if (paddleProductId === this.wsscPro.paddleData.paddleId) {
      return this.wsscPro;
    }
    if (paddleProductId === this.wsVpnGw.paddleData.paddleId) {
      return this.wsVpnGw;
    }

    return null;
  }

  getVolumeDiscountConfig(): VolumeDiscountConfig {
    return this.volumeDiscountConfig;
  }

  /**
   * All configured accumulation groups.
   */
  getAccumulationGroups(): AccumulationGroup[] {
    return this.accumulationGroups;
  }

  /**
   * The accumulation group a product belongs to.
   *
   * @param productId - Product being purchased or displayed
   * @returns The group, or null when the product is excluded from accumulation
   *
   * @example
   * getAccumulationGroup('wiresock-secure-connect-pro') // { id: 'pro-seats', ... }
   * getAccumulationGroup('wiresock-secure-connect')     // null (free product, no seats)
   */
  getAccumulationGroup(productId: ProductId): AccumulationGroup | null {
    return this.accumulationGroups.find(group => group.productIds.includes(productId)) ?? null;
  }

  getProProductByProductId(productId: Extract<ProductId, 'wiresock-secure-connect-pro' | 'wiresock-vpn-gateway-pro'>): Product {
    switch (productId) {
      case 'wiresock-secure-connect-pro':
        return this.wsscPro;
      case 'wiresock-vpn-gateway-pro':
        return this.wsVpnGw;
      default:
        return undefined as never;
    }
  }
}
